import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useI18n } from '@cdn/i18n';
import { useObservable } from '@cdn/state';
import { AgainLine, Unavailable as UiUnavailable } from '@beyond-js/ui/react';
import { Button, Loading, Notice, Outages, Unavailable } from '@cdn/ui';
import { useSession } from './context';

/**
 * Protects the management views. Without a session it sends the browser to sign in and keeps the address as
 * the return destination. A session that ends while the person works is not answered here: the page's `Session`
 * of `@beyond-js/ui` (`Renewal`) renews it without being seen or asks once, over the screens, which stay mounted
 * with what was typed. Coming back to the tab reads the session again, so a membership withdrawn meanwhile is
 * said (D26). While the session is read on arrival, the waiting element names the document opening's `session`
 * step (`data-bui-opening-wait`, `@beyond-js/ui/opening`), which says "Confirming your session".
 */
export function SessionGate({ children }: { children: ReactNode }) {
	const session = useSession();
	const location = useLocation();

	useEffect(() => {
		const revisit = () => document.visibilityState === 'visible' && void session.revisit();
		document.addEventListener('visibilitychange', revisit);
		return () => document.removeEventListener('visibilitychange', revisit);
	}, [session]);

	if (session.status === 'restoring') return <div data-bui-opening-wait="session"><Loading /></div>;
	if (session.status === 'unreachable') return <Unreachable />;
	if (session.status === 'anonymous') {
		const destination = session.destination.validate(`${location.pathname}${location.search}${location.hash}`);
		return <Navigate to={`/signin?return=${encodeURIComponent(destination)}`} replace />;
	}

	return <>{children}</>;
}

/**
 * The session could not be checked because the service, or the authority behind it, did not answer: unavailable,
 * never a sign-out. It names who did not answer as the page's outage lines do (CDN, or Beyond Accounts when the
 * service says so), or that the person is offline, with no Try again until the browser is online again. The person
 * is not sent to sign in and nothing was cleared; the check is tried again by itself,
 * said with when it was last tried (decision D132), and Try again asks at once. The screens open in place once the
 * service answers.
 */
export function Unreachable() {
	const { t, locale } = useI18n();
	const session = useSession();
	const outage = useObservable(session.outage);
	// The service answers, but not to this address: trying again would loop, so the way on is its own address
	if (outage.refused)
		return (
			<div className="mx-auto w-full max-w-md py-10">
				<Notice tone="warning" title={t('identity.refused.title')}>
					<p>{t('identity.refused.text', { origin: globalThis.location.origin })}</p>
				</Notice>
			</div>
		);
	// Offline nothing can answer: the page says so, with no Try again, and checks again once the browser is online
	const offline = outage.again.offline;
	const code = locale.code === 'es' ? 'es' : 'en';
	const reason = offline ? `${UiUnavailable.labels[code].offline}. ${t('identity.unreachable.offline')}` : t('identity.unreachable.text', { who: Outages.say(Outages.who(outage.problem), t) });
	return (
		<div className="mx-auto w-full max-w-md py-10">
			<Unavailable
				kind="outage"
				title={t('identity.unreachable.title')}
				reason={reason}
				action={
					offline ? undefined : (
						<Button busy={outage.checking} onClick={() => void session.retry()}>
							{t('identity.unreachable.retry')}
						</Button>
					)
				}
				secondary={<AgainLine moments={{ ...outage.again.moments, trying: outage.checking }} locale={locale.code} labels={AgainLine.labels[code]} />}
			/>
		</div>
	);
}
